import { useEffect, useState, useRef, useCallback } from "react";
import { Send, Sparkles } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import Paywall from "@/components/Paywall";
import ReactMarkdown from "react-markdown";

// Preserva i newline singoli: "\n" → "  \n" è la sintassi markdown per hard-break.
const withLineBreaks = (text) => (text || "").replace(/\n/g, "  \n");

const suggestions = [
  "Quando scade l'assicurazione auto?",
  "Trova la garanzia della lavatrice",
  "Quanto abbiamo speso al supermercato?",
  "Cosa c'è in agenda questa settimana?",
];

export default function AIChatPage() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [paywall, setPaywall] = useState(null);
  const scrollRef = useRef(null);

  const loadHistory = useCallback(async () => {
    try {
      const { data } = await api.get("/ai/chat/history");
      setMessages(data);
    } catch (err) {
      console.error("Failed to load AI chat history:", err?.message);
    }
  }, []);

  useEffect(() => { loadHistory(); }, [loadHistory]);
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, sending]);

  const handleMemoAction = async (action) => {
  console.log("Memo action:", action);

  if (action.type === "open_document") {
    const documentId = action.payload?.id;

    if (!documentId) {
      toast.error("Documento non disponibile");
      return;
    }

    try {
      const response = await api.get(
        `/documents/${documentId}/file`,
        {
          responseType: "blob",
        }
      );

      const blob = response.data;
      const url = URL.createObjectURL(blob);

      window.open(url, "_blank");

      setTimeout(() => {
        URL.revokeObjectURL(url);
      }, 60000);
    } catch (err) {
      console.error("Memo open document error:", err);
      toast.error("Impossibile aprire il documento");
    }

    return;
  }

  if (action.type === "share_document") {
    const documentId = action.payload?.id;

    if (!documentId) {
      toast.error("Documento non disponibile");
      return;
    }

    try {
      const response = await api.get(
        `/documents/${documentId}/file`,
        {
          responseType: "blob",
        }
      );

      const blob = response.data;

      const file = new File(
        [blob],
        "documento",
        {
          type: blob.type || "application/octet-stream",
        }
      );

      if (
        navigator.share &&
        navigator.canShare &&
        navigator.canShare({ files: [file] })
      ) {
        await navigator.share({
          files: [file],
          title: "Documento Family Brain",
        });
      } else {
        toast.error(
          "Condivisione non disponibile su questo dispositivo"
        );
      }
    } catch (err) {
      if (err?.name === "AbortError") {
        return;
      }

      console.error("Memo share document error:", err);
      toast.error("Impossibile condividere il documento");
    }
  }
};

  const send = async (text) => {
    const msg = text || input.trim();
    if (!msg || sending) return;
    setInput("");
    setMessages(prev => [...prev, { role: "user", content: msg, id: `tmp-${Date.now()}` }]);
    setSending(true);
    try {
      const { data } = await api.post("/ai/chat", { message: msg });
      setMessages(prev => [...prev, {
  role: "assistant",
  content: data.reply,
  actions: data.actions || [],
  documents: data.documents || [],
  id: `r-${Date.now()}`,
  undoableActions: data.undoable_actions || [],
}]);
    } catch (e) {
      if (e?.response?.status === 402) {
        setPaywall({
          title: "Limite richieste AI raggiunto",
          message: e.response.data?.detail || "Hai usato tutte le richieste AI di oggi. Passa a Standard per 200 richieste/giorno.",
        });
        // remove the optimistic user message so they can retry tomorrow
        setMessages(prev => prev.slice(0, -1));
      } else {
        toast.error("AI non disponibile");
      }
    } finally {
      setSending(false);
    }
  };

  const undoActions = async (msgId) => {
    const msg = messages.find(m => m.id === msgId);
    if (!msg || !msg.undoableActions?.length || msg.undoing || msg.undone) return;
    setMessages(prev => prev.map(x => x.id === msgId ? { ...x, undoing: true } : x));
    try {
      const { data } = await api.post("/ai/actions/undo", { actions: msg.undoableActions });
      const okCount = (data.undone || []).length;
      setMessages(prev => prev.map(x => x.id === msgId ? { ...x, undoing: false, undone: true } : x));
      toast.success(
        okCount > 1 ? `${okCount} azioni annullate` : "Azione annullata"
      );
    } catch (_e) {
      setMessages(prev => prev.map(x => x.id === msgId ? { ...x, undoing: false } : x));
      toast.error("Impossibile annullare");
    }
  };

  return (
    <div className="flex flex-col h-screen md:h-screen md:max-h-screen">
      <div className="px-5 md:px-10 py-5 border-b border-subtle bg-glass sticky top-0 z-10">
        <div className="flex items-center gap-3 max-w-4xl mx-auto">
          <div className="w-9 h-9 rounded-2xl bg-brand grid place-items-center">
            <Sparkles className="w-4 h-4 text-white" strokeWidth={1.6} />
          </div>
          <div>
            <div className="font-display font-medium">Memo</div>
            <div className="text-xs text-tertiary-fg">Il tuo membro virtuale di famiglia</div>
          </div>
        </div>
      </div>

      <div ref={scrollRef} className="flex-1 overflow-auto px-5 md:px-10 py-6 pb-32">
        <div className="max-w-3xl mx-auto space-y-4">
          {/* Suggestion cards — sempre fissate in alto, sopra la conversazione */}
          <div data-testid="ai-suggestions-panel" className="bg-surface border border-subtle rounded-3xl p-5">
            <div className="flex items-center gap-2 mb-3">
              <Sparkles className="w-4 h-4 text-brand" strokeWidth={1.8} />
              <h2 className="font-display text-base font-medium">Come posso aiutarti?</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {suggestions.map((s) => (
                <button
                  key={s}
                  data-testid={`ai-suggestion-${s.slice(0, 16).replace(/\s+/g, "-").toLowerCase()}`}
                  onClick={() => send(s)}
                  disabled={sending}
                  className="bg-app border border-subtle rounded-2xl p-3 text-left text-sm text-secondary-fg hover:bg-elevated hover:text-primary-fg transition-colors disabled:opacity-50"
                >
                  {s}
                </button>
              ))}
            </div>
          </div>

          {messages.map((m, i) => (
            <div key={m.id || i} className={`flex ${m.role === "user" ? "justify-end animate-slide-r" : "justify-start animate-slide-l"}`}>
              <div className={`max-w-[80%] rounded-2xl px-4 py-3 ${
                m.role === "user"
                  ? "bg-brand text-white rounded-tr-sm"
                  : "bg-surface border border-subtle text-primary-fg rounded-tl-sm"
              }`}>
                <div className="text-sm leading-relaxed [&_p]:my-0 [&_p+p]:mt-2 [&_strong]:font-semibold [&_em]:italic [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5">
                  <ReactMarkdown
                    allowedElements={["p", "strong", "em", "br", "code", "ul", "ol", "li"]}
                    unwrapDisallowed
                  >
                    {withLineBreaks(m.content)}
                  </ReactMarkdown>
                </div>
                {m.role === "assistant" && m.actions?.length > 0 && (
  <div className="flex flex-wrap gap-2 mt-3">

    {m.actions.map((action, index) => (
      <button
        key={index}
        type="button"
        onClick={() => handleMemoAction(action)}
        className="px-3 py-2 rounded-full bg-elevated border border-subtle text-sm hover:bg-subtle transition"
      >
        {action.label}
      </button>
    ))}

  </div>
)}
                {m.role === "assistant" && m.undoableActions && m.undoableActions.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-subtle flex flex-wrap items-center gap-2">
                    <span className="text-[11px] text-tertiary-fg">
                      {m.undoableActions.length === 1 ? "Azione creata:" : `${m.undoableActions.length} azioni create:`}
                    </span>
                    {m.undoableActions.map(a => (
                      <span key={a.id} className="text-[11px] px-2 py-0.5 rounded-full bg-elevated text-secondary-fg">
                        {a.type === "shopping" ? "🛒" : "📅"} {a.label}
                      </span>
                    ))}
                    <button
                      type="button"
                      data-testid={`ai-undo-btn-${m.id}`}
                      disabled={m.undoing || m.undone}
                      onClick={() => undoActions(m.id)}
                      className="ml-auto text-[11px] px-3 py-1 rounded-full border border-subtle hover:bg-elevated disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      {m.undone ? "Annullato ✓" : m.undoing ? "Annullo…" : "Annulla"}
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
          {sending && (
            <div className="flex justify-start">
              <div className="bg-surface border border-subtle rounded-2xl rounded-tl-sm px-4 py-3 flex items-center">
                <span className="typing-dot" /><span className="typing-dot" /><span className="typing-dot" />
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="fixed bottom-0 left-0 right-0 md:left-60 lg:left-64 bg-glass border-t border-subtle px-4 md:px-10 py-3 pb-20 md:pb-3 z-20">
        <div className="max-w-3xl mx-auto flex gap-2 items-end">
          <input
            data-testid="ai-chat-input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && send()}
            placeholder="Chiedi qualsiasi cosa..."
            className="input-warm flex-1 rounded-full"
          />
          <button
            onClick={() => send()}
            disabled={!input.trim() || sending}
            data-testid="ai-chat-send"
            className="btn-primary !px-4 !py-2.5 disabled:opacity-50"
            aria-label="Invia"
          >
            <Send className="w-4 h-4" />
          </button>
        </div>
      </div>
      <Paywall open={!!paywall} onClose={() => setPaywall(null)} title={paywall?.title} message={paywall?.message} />
    </div>
  );
}
